import React from 'react';
import { Layout, Badge, Dropdown, Avatar, Space, Popover, Tooltip, Tag, message } from 'antd';
import type { MenuProps } from 'antd';
import { Bell, LogOut, User, Menu, Sun, Moon, Building2, Check, Search } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useAppStore } from '../../stores/useAppStore';
import { useProfilePhoto } from '../../hooks/useProfilePhoto';
import { useUnreadAlerts } from '../../hooks/useUnreadAlerts';
import { useUnreadCount as usePlatformUnreadCount } from '../../services/notificationsApi';
import { useSupportNotificationsStore } from '../../stores/useSupportNotificationsStore';
import NotificationDropdown from '../NotificationBell/NotificationDropdown';
import CreditBalancePopover from '../CreditAlert/CreditBalancePopover';
import VoiceCreditPopover from '../CreditAlert/VoiceCreditPopover';
import EnrichmentQueueIcon from '../EnrichmentQueueIcon/EnrichmentQueueIcon';
import CrossSellQueueIcon from '../../../modules/cross-sell/components/CrossSellQueueIcon';
import { useFeatureFlagsContext } from '../../features';
import { useTheme } from '../../contexts/ThemeContext';
const CommandMenu = React.lazy(() => import('../command/CommandMenu').then(module => ({
  default: module.CommandMenu,
})));
// B2B Account Layer (onda 3a) — both render nothing when not applicable.
import AccountSwitcher from '../AccountSwitcher';
import SeatQuotaBadge from '../SeatQuotaBadge';
import IcpPopover from '../IcpPopover';
import { useAccountClaims } from '../../hooks/useAccountClaims';
import { useAccountTenants } from '../../hooks/useAccountTenants';
import { accountsApi } from '../../services/accountsApi';
import { displayRolePtBr, isPlatformAdmin } from '../../rbac/roles';

const { Header: AntHeader } = Layout;

const ROUTE_TITLE_KEYS: Record<string, string> = {
  '/': 'header.dashboard',
  '/pipeline': 'header.pipeline',
  '/prospects': 'header.prospects',
  '/alerts': 'header.alerts',
  '/contacts': 'header.contacts',
  '/profile': 'header.profile',
  '/agent': 'header.agent',
  '/bdr': 'header.bdr',
  '/sdr': 'header.sdr',
  '/executive': 'header.executive',
  '/admin': 'header.admin',
  '/analytics/win-loss': 'header.winLoss',
};

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { toggleSidebar } = useAppStore();
  const photo = useProfilePhoto();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isEnabled: isFeatureEnabled } = useFeatureFlagsContext();

  const pageTitle = React.useMemo(() => {
    const path = location.pathname;
    if (ROUTE_TITLE_KEYS[path]) return t(ROUTE_TITLE_KEYS[path]);
    const prefix = Object.keys(ROUTE_TITLE_KEYS).find(
      (k) => k !== '/' && path.startsWith(k),
    );
    return prefix ? t(ROUTE_TITLE_KEYS[prefix]) : t('header.platform');
  }, [location.pathname, t]);

  const { unreadCount } = useUnreadAlerts();
  // Platform notifications run alongside deal alerts — combined count drives
  // the bell badge (alerts UX in the dropdown is preserved on its own tab).
  const { data: platformUnread = 0 } = usePlatformUnreadCount();
  // Client-side support-ticket new-message detector. Counts toward the
  // same bell badge so the user sees a single unread number instead of
  // having to mentally sum across surfaces.
  const supportUnread = useSupportNotificationsStore((s) => s.unreadCount);
  const markSupportAllRead = useSupportNotificationsStore((s) => s.markAllRead);
  const totalUnread = unreadCount + platformUnread + supportUnread;
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);
  const { isDark, toggleTheme } = useTheme();

  // When the user clicks the bell, treat that as "I've seen them" — stop
  // the blink and clear the client-side support unread badge. Server-side
  // platform/alert unreads are managed by their own "mark all as read"
  // affordances inside the dropdown tabs.
  const handleBellOpenChange = React.useCallback(
    (next: boolean) => {
      setNotificationsOpen(next);
      if (next && supportUnread > 0) markSupportAllRead();
    },
    [supportUnread, markSupportAllRead],
  );

  const [commandMenuOpen, setCommandMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandMenuOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // ── Tenant switcher (account_owner) ──────────────────────────────────
  // The JWT carries `tenant_id` (current) + `account_id` + `account_role`.
  // When the user is an account_owner with N>1 accessible tenants in the
  // account, we expose a list under the avatar dropdown so they can hop
  // between tenants. Tenants come from useAccountTenants (BFF aggregator),
  // active tenant is the JWT's tenant_id. Switch reissues the JWT via
  // /auth/switch-tenant + hard reload so every downstream call rescopes.
  const { accountId, accountRole, tenantId: activeTenantId } = useAccountClaims();
  const queryClient = useQueryClient();
  const { data: accountTenants = [] } = useAccountTenants(accountId);
  const switchTenantMutation = useMutation({
    mutationFn: (targetTenantId: number) => accountsApi.switchTenant(targetTenantId),
    onSuccess: (data, targetTenantId) => {
      // Three things must move together for a clean switch:
      //   1. accessToken — new JWT carrying the target tenant_id claim.
      //   2. user — cached User object in localStorage holds tenantId; left
      //      stale, the first paint after reload shows the OLD tenant (chip
      //      lag) until /auth/me eventually replaces it.
      //   3. janus.activeTenant — backs useActiveTenant() + the X-Active-
      //      Tenant header on the Organizations API client (Wave A).
      //      Otherwise tenant-scoped queries that read this localStorage
      //      key (permissions, custom roles) keep the previous tenant.
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.removeItem('user');
      localStorage.setItem('janus.activeTenant', String(targetTenantId));
      // Dispatch the cross-tab + in-tab event the useActiveTenant hook
      // listens to so any mounted consumers refetch immediately (the
      // hard reload below also catches them, but this keeps the data
      // path consistent for the milliseconds between mutate and reload).
      try {
        window.dispatchEvent(new Event('janus:active-tenant-changed'));
      } catch {
        // ignore (SSR / older browsers)
      }
      queryClient.clear();
      message.success('Tenant alterado com sucesso');
      window.location.href = '/';
    },
    onError: () => {
      message.error('Falha ao trocar de tenant. Tente novamente.');
    },
  });

  const userRoleStrings = React.useMemo(() => {
    const u = (user as unknown as Record<string, unknown>) ?? {};
    return [
      ...((u.roles as string[] | undefined) ?? []),
      user?.role,
      accountRole,
    ].filter(Boolean) as string[];
  }, [user, accountRole]);

  // Show the tenant block for account_owner OR platform admin (master/super_admin)
  // with at least 2 visible tenants under the account.
  const visibleTenants = (accountTenants || []).filter((t) => t.active !== false);
  const isOwnerOrPlatformAdmin =
    accountRole === 'account_owner' || isPlatformAdmin(userRoleStrings);
  const showTenantSwitcher =
    isOwnerOrPlatformAdmin && visibleTenants.length > 1;

  // Friendly role label: internal platform admin (master/super_admin) >
  // account-scoped role (account_owner) > tenant role.
  const roleLabel = React.useMemo(() => {
    if (isPlatformAdmin(userRoleStrings)) return displayRolePtBr(user?.role ?? 'master_admin');
    if (accountRole === 'account_owner') return displayRolePtBr('account_owner');
    return displayRolePtBr(user?.role ?? '');
  }, [userRoleStrings, accountRole, user?.role]);

  // Name of the tenant the user is operating in right now — used in the
  // header subline ("Proprietário · Finnet - Vendas") so the user always
  // knows the scope. Empty when we don't have the tenant list yet.
  const activeTenantName = React.useMemo(() => {
    if (!activeTenantId) return null;
    const match = (accountTenants || []).find(
      (t) => String(t.id) === String(activeTenantId),
    );
    return match?.name ?? null;
  }, [accountTenants, activeTenantId]);

  const tenantMenuItems: NonNullable<MenuProps['items']> = showTenantSwitcher
    ? [
        { type: 'divider' as const },
        {
          key: 'tenants-header',
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94A3B8' }}>
              <Building2 size={12} />
              <span>Tenants do account</span>
            </div>
          ),
          disabled: true,
        },
        ...visibleTenants.map((tn) => ({
          key: `tenant-${tn.id}`,
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
              <Building2 size={14} />
              <span style={{ flex: 1 }}>{tn.name || `Tenant ${tn.id}`}</span>
              {String(tn.id) === String(activeTenantId) && (
                <Tag color="green" style={{ marginInlineEnd: 0 }}>
                  Atual
                </Tag>
              )}
              {String(tn.id) === String(activeTenantId) && <Check size={14} color="#10B981" />}
            </div>
          ),
          disabled:
            String(tn.id) === String(activeTenantId) || switchTenantMutation.isPending,
          onClick: () => {
            if (String(tn.id) !== String(activeTenantId)) {
              switchTenantMutation.mutate(Number(tn.id));
            }
          },
        })),
      ]
    : [];

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <User size={14} />,
      label: user?.name || t('sidebar.user'),
      onClick: () => navigate('/profile'),
    },
    ...tenantMenuItems,
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogOut size={14} />,
      label: t('header.logout'),
      onClick: handleLogout,
      danger: true,
    },
  ];

  return (
    <AntHeader
      style={{
        background: '#FFFFFF',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 1px 0 #E2E8F0',
        height: 64,
        lineHeight: '64px',
        position: 'sticky',
        top: 0,
        zIndex: 90,
      }}
    >
      <div className="flex items-center gap-4">
        <button
          onClick={toggleSidebar}
          aria-label={t('header.toggleSidebar', 'Alternar menu lateral')}
          className="flex items-center justify-center p-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer border-0 bg-transparent"
          style={{ color: '#64748B' }}
        >
          <Menu size={18} />
        </button>
        <div>
          <h2 className="page-title" style={{ fontSize: 16 }}>{pageTitle}</h2>
        </div>
        {/* Attio Spotlight Trigger */}
        <button
          type="button"
          onClick={() => setCommandMenuOpen(true)}
          className="hidden md:flex items-center gap-2.5 px-3 py-1 text-xs text-slate-400 bg-slate-50 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/80 rounded-lg hover:border-slate-300 dark:hover:border-slate-600 transition-all cursor-pointer shadow-2xs group ml-1"
          title="Buscar ou executar comandos (⌘K / Ctrl+K)"
        >
          <Search size={13} className="text-slate-400 group-hover:text-indigo-500 transition-colors" />
          <span className="text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200 font-sans">
            Buscar no Janus...
          </span>
          <kbd className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-700 text-slate-400 font-medium">
            ⌘K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-3">
        {/* B2B Account Layer affordances — render nothing for users without
            multi-account or tenant_admin context, so they're safe defaults. */}
        <SeatQuotaBadge />
        <AccountSwitcher />

        {/* ICP — discreet target icon next to the credits pill; opens a rich
            popover with the tenant's Ideal Customer Profile (replaces the old
            full-width strip on the discovery/pipeline/deal pages). */}
        <IcpPopover />

        <CreditBalancePopover />

        {/* Voice credit (VC) pool — isolated from the general pool above.
            Self-guards on isVoiceEnabled: renders nothing when voice is off
            (404 / tenant without a voice pool), so the header stays clean. */}
        <VoiceCreditPopover />

        <Tooltip title={isDark ? t('header.lightMode') : t('header.darkMode')}>
          <button
            onClick={toggleTheme}
            className="flex items-center justify-center p-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer border-0 bg-transparent"
            style={{ color: '#64748B' }}
            aria-label={isDark ? t('header.activateLightMode') : t('header.activateDarkMode')}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </Tooltip>

        <EnrichmentQueueIcon />

        {isFeatureEnabled('cross_sell') && <CrossSellQueueIcon />}

        <Popover
          content={
            <NotificationDropdown
              open={notificationsOpen}
              onClose={() => setNotificationsOpen(false)}
            />
          }
          trigger="click"
          open={notificationsOpen}
          onOpenChange={handleBellOpenChange}
          placement="bottomRight"
          arrow={false}
          overlayInnerStyle={{ padding: 0, borderRadius: 8, overflow: 'hidden' }}
        >
          <Badge
            count={totalUnread}
            size="small"
            styles={{ indicator: { background: '#6366F1' } }}
          >
            <button
              aria-label={t('header.notifications', 'Notificações')}
              className="flex items-center justify-center p-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer border-0 bg-transparent"
              style={{ color: '#64748B' }}
            >
              {/* Blink only while there's something unread and the
                  dropdown is closed — once the user opens the popover
                  they're already looking at the bell, the animation
                  becomes noise. */}
              <Bell
                size={18}
                className={totalUnread > 0 && !notificationsOpen ? 'janus-bell-blink' : undefined}
              />
            </button>
          </Badge>
        </Popover>

        <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
          <Space className="cursor-pointer" style={{ gap: 8 }}>
            <Avatar
              src={photo}
              style={{
                background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                fontSize: 13,
                fontWeight: 600,
              }}
              size={34}
            >
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <div className="hidden sm:block" style={{ lineHeight: 1.25 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>
                {user?.name}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: '#64748B',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  maxWidth: 220,
                }}
              >
                <span style={{ fontWeight: 500 }}>{roleLabel}</span>
                {activeTenantName && (
                  <>
                    <span style={{ color: '#CBD5E1' }}>·</span>
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={activeTenantName}
                    >
                      {activeTenantName}
                    </span>
                  </>
                )}
              </div>
            </div>
          </Space>
        </Dropdown>
      </div>

      {commandMenuOpen && (
        <React.Suspense fallback={<span role="status">Carregando busca...</span>}>
          <CommandMenu open onClose={() => setCommandMenuOpen(false)} />
        </React.Suspense>
      )}
    </AntHeader>
  );
};

export default Header;
